/**
 * Chat API
 * Manages all chat operations: creating, loading, saving messages, and deleting chats
 * Follows the 3-table structure: chats -> turns -> messages
 */

import type { ModelMessage, UIMessage } from 'ai'
import { handleUnauthorized } from './api-error-handler'
import { BACKEND_URL } from './config'
import { getAuthToken } from './supabase'
import type { Chat } from './types'
import type { NormalizedUsage } from './utils/message-helpers'

/**
 * Convert ModelMessage to UIMessage format expected by backend
 * Backend expects messages with 'parts' array, not just 'content' string
 */
function toUIMessage(
  message: ModelMessage
): Omit<UIMessage, 'id'> & { id?: string } {
  const uiMessage: Omit<UIMessage, 'id'> & { id?: string } = {
    role: (message as { role: string }).role as UIMessage['role'],
    parts: [],
  }

  // Include id if present (backend will generate one if missing)
  if ('id' in message && typeof (message as { id?: string }).id === 'string') {
    uiMessage.id = (message as { id: string }).id
  }

  // If message already has parts, use them
  // Explicitly check for parts property to handle tool messages with ToolUIPart
  const messageWithParts = message as { parts?: unknown[] }
  if (
    messageWithParts.parts &&
    Array.isArray(messageWithParts.parts) &&
    messageWithParts.parts.length > 0
  ) {
    uiMessage.parts = messageWithParts.parts as UIMessage['parts']
  } else if (typeof message.content === 'string' && message.content) {
    // Convert content string to parts array format
    uiMessage.parts = [{ type: 'text', text: message.content }]
  }

  // Preserve metadata if present
  if ('metadata' in message && message.metadata) {
    uiMessage.metadata = message.metadata
  }

  return uiMessage
}

export interface SaveUserMessageRequest {
  chatId: string
  message: ModelMessage
  workbookKey?: string
}

export interface SaveUserMessageResponse {
  success: boolean
  turnId: string
  messageId: string
  title?: string | null
  error?: string
}

export interface SaveAssistantMessageRequest {
  chatId: string
  turnId: string
  message: ModelMessage
  usage?: NormalizedUsage
  model?: string
}

export interface SaveAssistantMessageResponse {
  success: boolean
  messageId: string
  creditsDeducted?: number
  remainingCredits?: number
  error?: string
}

export interface ChatDetail {
  id: string
  title?: string | null
  created_at: string
  updated_at: string
}

export interface CreateChatResponse {
  success: boolean
  chat: ChatDetail
  error?: string
}

export interface Turn {
  id: string
  created_at: string
  model?: string
  messages: Array<{
    id: string
    role: string
    parts?: unknown[]
    metadata?: Record<string, unknown>
  }>
}

export interface LoadTurnsResponse {
  success: boolean
  chat: ChatDetail
  turns: Turn[]
}

/**
 * Save a user message to the backend
 * Creates a new turn and returns the turnId for subsequent assistant messages
 */
export async function saveUserMessage(
  data: SaveUserMessageRequest
): Promise<SaveUserMessageResponse> {
  const authToken = await getAuthToken()
  if (!authToken) {
    throw new Error('Not authenticated')
  }

  const url = `${BACKEND_URL}/api/chats/user-message`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({
        chatId: data.chatId,
        workbookKey: data.workbookKey,
        message: toUIMessage(data.message),
      }),
    })

    if (!response.ok) {
      if (response.status === 401) {
        await handleUnauthorized(response, 'Save user message')
        throw new Error('Unauthorized')
      }
      const errorText = await response
        .text()
        .catch(() => 'Unable to read error')
      console.error('Save user message API error', {
        status: response.status,
        body: errorText.slice(0, 500),
      })
      try {
        const error = JSON.parse(errorText)
        throw new Error(
          error.error || error.detail || `HTTP ${response.status}`
        )
      } catch {
        throw new Error(`HTTP ${response.status}: ${errorText.slice(0, 200)}`)
      }
    }

    const result = await response.json()
    return result as SaveUserMessageResponse
  } catch (error) {
    console.error('Failed to save user message', error)
    throw error
  }
}

/**
 * Save an assistant message to the backend
 * Includes usage data for credit deduction
 */
export async function saveAssistantMessage(
  data: SaveAssistantMessageRequest
): Promise<SaveAssistantMessageResponse> {
  const authToken = await getAuthToken()
  if (!authToken) {
    throw new Error('Not authenticated')
  }

  const url = `${BACKEND_URL}/api/chats/assistant-message`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({
        chatId: data.chatId,
        turnId: data.turnId,
        message: toUIMessage(data.message),
        usage: data.usage,
        model: data.model,
      }),
    })

    if (!response.ok) {
      if (response.status === 401) {
        await handleUnauthorized(response, 'Save assistant message')
        throw new Error('Unauthorized')
      }
      const errorText = await response
        .text()
        .catch(() => 'Unable to read error')
      console.error('Save assistant message API error', {
        status: response.status,
        body: errorText.slice(0, 500),
      })
      try {
        const error = JSON.parse(errorText)
        throw new Error(
          error.error || error.detail || `HTTP ${response.status}`
        )
      } catch {
        throw new Error(`HTTP ${response.status}: ${errorText.slice(0, 200)}`)
      }
    }

    const result = await response.json()
    return result as SaveAssistantMessageResponse
  } catch (error) {
    console.error('Failed to save assistant message', error)
    throw error
  }
}

/**
 * Create an empty chat
 * Returns a new chat with generated ID, ready to receive messages
 */
export async function createChat(
  workbookKey?: string | null
): Promise<CreateChatResponse> {
  const authToken = await getAuthToken()
  if (!authToken) {
    throw new Error('Not authenticated')
  }

  const url = `${BACKEND_URL}/api/chats`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: workbookKey ? JSON.stringify({ workbookKey }) : undefined,
    })

    if (!response.ok) {
      if (response.status === 401) {
        await handleUnauthorized(response, 'Create chat')
        throw new Error('Unauthorized')
      }
      const errorText = await response
        .text()
        .catch(() => 'Unable to read error')
      console.error('Create chat API error', {
        status: response.status,
        body: errorText.slice(0, 500),
      })
      try {
        const error = JSON.parse(errorText)
        throw new Error(
          error.error || error.detail || `HTTP ${response.status}`
        )
      } catch {
        throw new Error(`HTTP ${response.status}: ${errorText.slice(0, 200)}`)
      }
    }

    const result = await response.json()
    return result as CreateChatResponse
  } catch (error) {
    console.error('Failed to create chat', error)
    throw error
  }
}

/**
 * Load chats asynchronously from the database
 * @returns ChatState containing chats (without messages - load individually as needed)
 */
export async function loadChatsAsync(
  limit?: number,
  workbookKey?: string | null
): Promise<Chat[]> {
  try {
    const token = await getAuthToken()
    if (!token) {
      console.warn('No auth token available, returning empty chat state')
      return []
    }

    const urlParams = new URLSearchParams()
    if (limit !== undefined) {
      urlParams.set('limit', limit.toString())
    }
    if (workbookKey) {
      urlParams.set('workbookKey', workbookKey)
    }
    const url = `${BACKEND_URL}/api/chats${urlParams.toString() ? `?${urlParams.toString()}` : ''}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'ngrok-skip-browser-warning': 'true',
      },
    })

    if (!response.ok) {
      if (response.status === 401) {
        await handleUnauthorized(response, 'Load chats')
        return []
      }
      const errorText = await response.text()
      console.error(
        `Failed to load chats (${response.status}): ${response.statusText}`,
        errorText
      )
      return []
    }

    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      console.error(`Invalid content type: ${contentType}`)
      return []
    }

    let data: unknown
    try {
      data = await response.json()
    } catch (parseError) {
      console.error('Failed to parse response as JSON', parseError)
      return []
    }

    if (!data || typeof data !== 'object') {
      console.warn('Invalid response format')
      return []
    }

    const dataRecord = data as Record<string, unknown>

    // New format: { success: true, chats: [...] }
    if ('success' in dataRecord && 'chats' in dataRecord) {
      const backendChats = dataRecord.chats as Array<{
        id: string
        title?: string | null
        created_at: string
        updated_at: string
      }>

      const chats: Chat[] = backendChats
        .map((chat) => ({
          id: chat.id,
          title: chat.title || 'Untitled chat',
          messages: [], // Messages loaded separately via loadChatAsync
          createdAt: chat.created_at
            ? new Date(chat.created_at).getTime()
            : Date.now(),
        }))
        .filter((chat) => chat.id)
        .sort((a, b) => b.createdAt - a.createdAt)
      return chats
    }

    // Fallback to old format
    console.warn('Unexpected response format, returning empty chats')
    return []
  } catch (error) {
    console.error('Failed to load chats from database', error)
    return []
  }
}

/**
 * Load a specific chat with all its turns and messages
 */
export async function loadChatTurnsAsync(
  chatId: string
): Promise<LoadTurnsResponse | null> {
  try {
    const token = await getAuthToken()
    if (!token) {
      console.warn('No auth token available')
      return null
    }

    const url = `${BACKEND_URL}/api/chats/${encodeURIComponent(chatId)}/turns`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      if (response.status === 401) {
        await handleUnauthorized(response, 'Load chat turns')
        return null
      }
      const errorText = await response.text()
      console.error(
        `Failed to load chat turns (${response.status}): ${response.statusText}`,
        errorText
      )
      return null
    }

    const data = await response.json()
    if (!data.success || !data.chat || !data.turns) {
      console.warn('Invalid response format')
      return null
    }

    return data as LoadTurnsResponse
  } catch (error) {
    console.error('Failed to load chat turns from database', error)
    return null
  }
}

/**
 * Delete a chat
 */
export async function deleteChat(chatId: string): Promise<void> {
  try {
    const token = await getAuthToken()
    if (!token) {
      console.warn('No auth token available, skipping delete chat')
      return
    }

    const url = `${BACKEND_URL}/api/chats/${encodeURIComponent(chatId)}`

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
    })

    if (!response.ok) {
      if (response.status === 401) {
        await handleUnauthorized(response, 'Delete chat')
        throw new Error('Unauthorized')
      }
      const errorText = await response.text()
      console.error(`[DELETE CHAT] Failed: ${response.status} ${errorText}`)
      throw new Error(
        `Failed to delete chat: ${response.status} ${response.statusText}`
      )
    }
  } catch (error) {
    console.error('[DELETE CHAT] Failed to delete chat from database', error)
    throw error
  }
}
